import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiArrowLeft,
  FiCamera,
  FiZap,
  FiX,
  FiEdit3,
  FiCheck,
} from "react-icons/fi";
import { Sparkles, Settings2 } from "lucide-react";
import {
  MISSION_TYPES,
  URGENCY_LEVELS,
  DURATION_TYPES,
  createMission,
} from "../../utils/missionsApi";
import MissionTypeIcon from "./components/MissionTypeIcon";

const QUICK_TYPES = [
  "carrosserie_petit_choc",
  "debosselage",
  "reparation_jantes",
  "nettoyage_interieur",
  "preparation_vehicule",
  "convoyage",
];

export default function CreateMission() {
  const navigate = useNavigate();
  const role = (localStorage.getItem("role") || "").toLowerCase();
  const canCreateMission =
    role === "admin" ||
    (role === "pro" && localStorage.getItem("proCanPostMissions") === "true");
  const [mode, setMode] = useState("quick"); // "quick" | "full"
  const [submitting, setSubmitting] = useState(false);
  const [photos, setPhotos] = useState([]); // {file, url}
  const fileRef = useRef(null);

  const [form, setForm] = useState({
    title: "",
    type: "",
    description: "",
    vehicleBrand: "",
    vehicleModel: "",
    vehiclePlate: "",
    city: "",
    postalCode: "",
    interventionAddress: "",
    date: "",
    urgency: "normal",
    durationType: "journee",
    estimatedBudget: "",
    specialInstructions: "",
    companyName: "",

    // convoying
    departureCity: "",
    arrivalCity: "",
    departureAddress: "",
    arrivalAddress: "",
    departureDate: "",
    arrivalDate: "",
    fuelLevel: "",

    // bodywork/wheels
    damagedArea: "",
    damageSeverity: "",

    // cleaning
    cleaningInterior: false,
    cleaningExterior: false,
    cleaningFull: false,
    vehiclesCount: 1,
  });

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const isConvoy = form.type === "convoyage";
  const isBody = ["carrosserie_retouche", "carrosserie_petit_choc", "debosselage",
                  "reparation_jantes", "renovation_jantes", "peinture_jantes"].includes(form.type);
  const isCleaning = ["nettoyage_interieur", "nettoyage_exterieur", "preparation_vehicule", "polissage_lustrage"].includes(form.type);

  const handlePhotos = (e) => {
    const list = Array.from(e.target.files || []);
    const mapped = list.map((file) => ({ file, url: URL.createObjectURL(file) }));
    setPhotos((p) => [...p, ...mapped].slice(0, 10));
    e.target.value = "";
  };

  const removePhoto = (i) =>
    setPhotos((p) => p.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!canCreateMission) {
      return alert("La création de mission est réservée aux pros autorisés par l'admin.");
    }
    if (!form.title.trim() || !form.type || !form.city.trim()) {
      return alert("Merci de renseigner au moins le titre, le type et la ville.");
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      // Remove empties
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "" || payload[k] === null || payload[k] === undefined) delete payload[k];
      });
      const mission = await createMission(payload, photos.map((p) => p.file));
      alert(
        mission.status === "en_attente_validation"
          ? "Mission envoyée à l'admin. Elle sera visible après validation et ajustement du prix si besoin."
          : "Mission publiée. Les prestataires peuvent désormais postuler."
      );
      navigate(`/missions/${mission._id}`);
    } catch (err) {
      alert(err?.response?.data?.message || "Erreur lors de la publication");
    } finally {
      setSubmitting(false);
    }
  };

  const quickTypes = useMemo(
    () => MISSION_TYPES.filter((t) => QUICK_TYPES.includes(t.key)),
    []
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      {!canCreateMission && (
        <div className="pmm-card" style={{ borderColor: "rgba(180,83,9,.35)" }}>
          <strong>Accès non autorisé.</strong> Seuls les comptes pros validés avec accès ProMeet Missions peuvent poster une mission.
        </div>
      )}
      <div className="pmm-topbar">
        <div>
          <h1 className="pmm-h-accent">
            {mode === "quick" ? "Poster une mission en 30 secondes" : "Nouvelle mission détaillée"}
          </h1>
          <div className="pmm-topbar-sub">
            {mode === "quick"
              ? "Saisissez l'essentiel maintenant — complétez plus tard si besoin."
              : "Détaillez votre mission pour recevoir les meilleures propositions."}
          </div>
        </div>
        <div className="pmm-topbar-actions">
          <button className="pmm-btn" onClick={() => navigate(-1)}><FiArrowLeft /> Retour</button>
          <div role="tablist" aria-label="Mode de saisie" className="pmm-pill-row" style={{ gap: 4 }}>
            <button
              type="button"
              role="tab"
              aria-selected={mode === "quick"}
              className={`pmm-pill ${mode === "quick" ? "is-active" : ""}`}
              onClick={() => setMode("quick")}
            >
              <Sparkles size={14} /> Rapide
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === "full"}
              className={`pmm-pill ${mode === "full" ? "is-active" : ""}`}
              onClick={() => setMode("full")}
            >
              <Settings2 size={14} /> Détaillé
            </button>
          </div>
        </div>
      </div>

      <form className="pmm-card pmm-form" onSubmit={submit}>
        {/* ====== QUICK MODE ====== */}
        {mode === "quick" && (
          <>
            <div className="pmm-field">
              <label>Type de mission *</label>
              <div className="pmm-pill-row">
                {quickTypes.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    className={`pmm-pill ${form.type === t.key ? "is-active" : ""}`}
                    onClick={() => update({ type: t.key })}
                  >
                    <MissionTypeIcon type={t.key} size={22} />
                    {t.label}
                    {form.type === t.key && <FiCheck />}
                  </button>
                ))}
              </div>
              <span className="pmm-field-hint">
                <FiEdit3 size={12} style={{ verticalAlign: "-1px", marginRight: 4 }} />
                Vous pouvez choisir un autre type en mode détaillé.
              </span>
            </div>

            <div className="pmm-row pmm-row--3">
              <div className="pmm-field">
                <label>Titre *</label>
                <input
                  type="text"
                  placeholder="Ex : Retouche aile arrière droite"
                  value={form.title}
                  onChange={(e) => update({ title: e.target.value })}
                  required
                />
              </div>
              <div className="pmm-field">
                <label>Ville *</label>
                <input
                  type="text"
                  placeholder="Paris"
                  value={form.city}
                  onChange={(e) => update({ city: e.target.value })}
                  required
                />
              </div>
              <div className="pmm-field">
                <label>Date souhaitée</label>
                <input
                  type="date"
                  value={form.date}
                  onChange={(e) => update({ date: e.target.value })}
                />
              </div>
            </div>

            <div className="pmm-field">
              <label>Photos (max 10)</label>
              <button
                type="button"
                className="pmm-btn"
                onClick={() => fileRef.current?.click()}
              >
                <FiCamera /> Ajouter des photos
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                multiple
                hidden
                onChange={handlePhotos}
              />
              {photos.length > 0 && (
                <div className="pmm-grid pmm-grid--4" style={{ marginTop: 6 }}>
                  {photos.map((p, i) => (
                    <div key={i} style={{ position: "relative", borderRadius: 12, overflow: "hidden", border: "1px solid var(--border)" }}>
                      <img src={p.url} alt="" style={{ width: "100%", height: 100, objectFit: "cover", display: "block" }} />
                      <button
                        type="button"
                        onClick={() => removePhoto(i)}
                        aria-label="Retirer"
                        style={{
                          position: "absolute", top: 6, right: 6,
                          background: "rgba(15,23,42,0.7)", color: "#fff",
                          border: "none", borderRadius: 999, width: 26, height: 26, padding: 0,
                          minHeight: "auto", display: "grid", placeItems: "center",
                          cursor: "pointer",
                        }}>
                        <FiX />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="pmm-field">
              <label>Description rapide</label>
              <textarea
                placeholder="Décrivez brièvement la prestation attendue..."
                value={form.description}
                onChange={(e) => update({ description: e.target.value })}
              />
            </div>

            <div className="pmm-row pmm-row--3">
              <div className="pmm-field">
                <label>Budget estimé (€)</label>
                <input type="number" min={0} value={form.estimatedBudget}
                       onChange={(e) => update({ estimatedBudget: e.target.value })} />
              </div>
              <div className="pmm-field">
                <label>Urgence</label>
                <select value={form.urgency} onChange={(e) => update({ urgency: e.target.value })}>
                  {URGENCY_LEVELS.map((u) => (
                    <option key={u.key} value={u.key}>{u.label}</option>
                  ))}
                </select>
              </div>
              <div className="pmm-field">
                <label>Durée</label>
                <select value={form.durationType} onChange={(e) => update({ durationType: e.target.value })}>
                  {DURATION_TYPES.map((d) => (
                    <option key={d.key} value={d.key}>{d.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </>
        )}

        {/* ====== FULL MODE ====== */}
        {mode === "full" && (
          <>
            <div className="pmm-row pmm-row--3">
              <div className="pmm-field">
                <label>Titre *</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => update({ title: e.target.value })}
                  required
                />
              </div>
              <div className="pmm-field">
                <label>Type *</label>
                <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                  {form.type && <MissionTypeIcon type={form.type} size={44} />}
                  <select
                    value={form.type}
                    onChange={(e) => update({ type: e.target.value })}
                    required
                    style={{ flex: 1 }}
                  >
                    <option value="">— Choisir —</option>
                    {MISSION_TYPES.map((t) => (
                      <option key={t.key} value={t.key}>{t.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="pmm-field">
                <label>Société (optionnel)</label>
                <input type="text" value={form.companyName}
                       onChange={(e) => update({ companyName: e.target.value })} />
              </div>
            </div>

            <div className="pmm-row pmm-row--3">
              <div className="pmm-field">
                <label>Marque</label>
                <input type="text" value={form.vehicleBrand}
                       onChange={(e) => update({ vehicleBrand: e.target.value })} />
              </div>
              <div className="pmm-field">
                <label>Modèle</label>
                <input type="text" value={form.vehicleModel}
                       onChange={(e) => update({ vehicleModel: e.target.value })} />
              </div>
              <div className="pmm-field">
                <label>Plaque (optionnel)</label>
                <input type="text" value={form.vehiclePlate}
                       onChange={(e) => update({ vehiclePlate: e.target.value })} />
              </div>
            </div>

            <div className="pmm-row pmm-row--3">
              <div className="pmm-field">
                <label>Ville *</label>
                <input type="text" value={form.city}
                       onChange={(e) => update({ city: e.target.value })} required />
              </div>
              <div className="pmm-field">
                <label>Code postal</label>
                <input type="text" value={form.postalCode}
                       onChange={(e) => update({ postalCode: e.target.value })} />
              </div>
              <div className="pmm-field">
                <label>Adresse intervention</label>
                <input type="text" value={form.interventionAddress}
                       onChange={(e) => update({ interventionAddress: e.target.value })} />
              </div>
            </div>

            <div className="pmm-row pmm-row--3">
              <div className="pmm-field">
                <label>Date</label>
                <input type="date" value={form.date}
                       onChange={(e) => update({ date: e.target.value })} />
              </div>
              <div className="pmm-field">
                <label>Urgence</label>
                <select value={form.urgency} onChange={(e) => update({ urgency: e.target.value })}>
                  {URGENCY_LEVELS.map((u) => (
                    <option key={u.key} value={u.key}>{u.label}</option>
                  ))}
                </select>
              </div>
              <div className="pmm-field">
                <label>Durée</label>
                <select value={form.durationType} onChange={(e) => update({ durationType: e.target.value })}>
                  {DURATION_TYPES.map((d) => (
                    <option key={d.key} value={d.key}>{d.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="pmm-row">
              <div className="pmm-field">
                <label>Budget estimé (€)</label>
                <input type="number" min={0} value={form.estimatedBudget}
                       onChange={(e) => update({ estimatedBudget: e.target.value })} />
              </div>
              <div className="pmm-field">
                <label>Photos</label>
                <button type="button" className="pmm-btn" onClick={() => fileRef.current?.click()}>
                  <FiCamera /> {photos.length ? `${photos.length} photo${photos.length > 1 ? "s" : ""}` : "Ajouter"}
                </button>
                <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={handlePhotos} />
              </div>
            </div>

            {photos.length > 0 && (
              <div className="pmm-grid pmm-grid--4">
                {photos.map((p, i) => (
                  <div key={i} style={{ position: "relative", borderRadius: 12, overflow: "hidden", border: "1px solid var(--border)" }}>
                    <img src={p.url} alt="" style={{ width: "100%", height: 100, objectFit: "cover", display: "block" }} />
                    <button
                      type="button"
                      onClick={() => removePhoto(i)}
                      aria-label="Retirer"
                      style={{
                        position: "absolute", top: 6, right: 6,
                        background: "rgba(15,23,42,0.7)", color: "#fff",
                        border: "none", borderRadius: 999, width: 26, height: 26, padding: 0,
                        minHeight: "auto", display: "grid", placeItems: "center",
                        cursor: "pointer",
                      }}>
                      <FiX />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="pmm-field">
              <label>Description</label>
              <textarea value={form.description}
                        onChange={(e) => update({ description: e.target.value })} />
            </div>

            <div className="pmm-field">
              <label>Instructions spéciales</label>
              <textarea value={form.specialInstructions}
                        onChange={(e) => update({ specialInstructions: e.target.value })} />
            </div>

            {/* === Conditional sections === */}
            {isConvoy && (
              <div className="pmm-card" style={{ background: "var(--cream-warm)" }}>
                <h3 style={{ marginTop: 0, fontSize: 16 }}>🚛 Détails convoyage</h3>
                <div className="pmm-row">
                  <div className="pmm-field">
                    <label>Ville de départ</label>
                    <input type="text" value={form.departureCity}
                           onChange={(e) => update({ departureCity: e.target.value })} />
                  </div>
                  <div className="pmm-field">
                    <label>Ville d'arrivée</label>
                    <input type="text" value={form.arrivalCity}
                           onChange={(e) => update({ arrivalCity: e.target.value })} />
                  </div>
                </div>
                <div className="pmm-row">
                  <div className="pmm-field">
                    <label>Adresse de départ</label>
                    <input type="text" value={form.departureAddress}
                           onChange={(e) => update({ departureAddress: e.target.value })} />
                  </div>
                  <div className="pmm-field">
                    <label>Adresse d'arrivée</label>
                    <input type="text" value={form.arrivalAddress}
                           onChange={(e) => update({ arrivalAddress: e.target.value })} />
                  </div>
                </div>
                <div className="pmm-row pmm-row--3">
                  <div className="pmm-field">
                    <label>Date départ</label>
                    <input type="datetime-local" value={form.departureDate}
                           onChange={(e) => update({ departureDate: e.target.value })} />
                  </div>
                  <div className="pmm-field">
                    <label>Date arrivée</label>
                    <input type="datetime-local" value={form.arrivalDate}
                           onChange={(e) => update({ arrivalDate: e.target.value })} />
                  </div>
                  <div className="pmm-field">
                    <label>Niveau de carburant</label>
                    <input type="text" placeholder="Ex: 3/4 ou Plein"
                           value={form.fuelLevel}
                           onChange={(e) => update({ fuelLevel: e.target.value })} />
                  </div>
                </div>
                <span className="pmm-field-hint">
                  Photos & kilométrages au départ/arrivée seront capturés sur le terrain
                  via la page « Photos / Validation » par le convoyeur.
                </span>
              </div>
            )}

            {isBody && (
              <div className="pmm-card" style={{ background: "var(--cream-warm)" }}>
                <h3 style={{ marginTop: 0, fontSize: 16 }}>🛠️ Détails carrosserie / jantes</h3>
                <div className="pmm-row">
                  <div className="pmm-field">
                    <label>Zone endommagée</label>
                    <input type="text" placeholder="Ex : aile avant gauche, jante AR droite..."
                           value={form.damagedArea}
                           onChange={(e) => update({ damagedArea: e.target.value })} />
                  </div>
                  <div className="pmm-field">
                    <label>Sévérité</label>
                    <select value={form.damageSeverity} onChange={(e) => update({ damageSeverity: e.target.value })}>
                      <option value="">—</option>
                      <option value="leger">Léger</option>
                      <option value="moyen">Moyen</option>
                      <option value="important">Important</option>
                      <option value="majeur">Majeur</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {isCleaning && (
              <div className="pmm-card" style={{ background: "var(--cream-warm)" }}>
                <h3 style={{ marginTop: 0, fontSize: 16 }}>🧽 Détails nettoyage</h3>
                <div className="pmm-pill-row">
                  {[
                    { key: "cleaningInterior", label: "Intérieur" },
                    { key: "cleaningExterior", label: "Extérieur" },
                    { key: "cleaningFull", label: "Complet" },
                  ].map((p) => (
                    <button
                      key={p.key}
                      type="button"
                      className={`pmm-pill ${form[p.key] ? "is-active" : ""}`}
                      onClick={() => update({ [p.key]: !form[p.key] })}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
                <div className="pmm-row">
                  <div className="pmm-field">
                    <label>Nombre de véhicules</label>
                    <input type="number" min={1} value={form.vehiclesCount}
                           onChange={(e) => update({ vehiclesCount: Number(e.target.value) || 1 })} />
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        <div className="pmm-sticky-actions">
          <button type="button" className="pmm-btn" onClick={() => navigate(-1)}>Annuler</button>
          <button type="submit" className="pmm-btn pmm-btn--primary pmm-btn--block" disabled={submitting}>
            {submitting ? "Publication…" : (<><FiZap /> Publier la mission</>)}
          </button>
        </div>
      </form>
    </div>
  );
}
